import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, Title } from "@tremor/react";
import { Alert, Button, Checkbox, Empty, Input, Select, Spin, Statistic, Table, Tag } from "antd";
import dayjs from "dayjs";

import { CollectionsBankTransactionStatus } from "@fitness-app/data-models/entities/CollectionsBankTransaction";
import {
  COLLECTIONS_CASE_CLOSED_STATUSES,
  CollectionsCaseStatus,
  type CollectionsCaseStatus as CaseStatus,
} from "@fitness-app/data-models/entities/CollectionsCase";

import {
  CaseStatusTag,
  formatPln,
  KPI_GRID,
  pluralRaty,
  STATUS_OPTIONS,
  SituationTag,
  taskLabel,
} from "./collectionsLabels";
import AutomationPanel from "./components/AutomationPanel";
import BankTransfersPanel from "./components/BankTransfersPanel";
import { urlPagination, useCollectionsUrlState } from "./hooks/useCollectionsUrlState";
import {
  useBankTransactions,
  useCollectionsCases,
  useCollectionsDashboard,
  useSyncCollectionsCases,
  type CollectionsCaseListItem,
} from "./hooks/useCollections";

const OPEN_STATUSES = Object.values(CollectionsCaseStatus).filter(
  (status) => !COLLECTIONS_CASE_CLOSED_STATUSES.includes(status),
);

/**
 * Opened on the first declined card charge so K1 can go out, but nothing is overdue
 * while Stripe still retries — not a debt to work on yet, so hidden unless asked for.
 */
const onlyRetrying = (row: CollectionsCaseListItem) =>
  row.status === CollectionsCaseStatus.New && !row.balance.overdueAmount && Boolean(row.balance.retryingAmount);

const CollectionsDashboard = () => {
  const location = useLocation();
  const [urlState, setUrlState] = useCollectionsUrlState();
  const { query, statuses, showClosed, showRetrying } = urlState;
  // Any filter change starts the list from its first page.
  const setFilter = (next: Partial<typeof urlState>) => void setUrlState({ ...next, page: 1 });

  const dashboard = useCollectionsDashboard();
  const cases = useCollectionsCases({
    statuses: statuses.length ? statuses : showClosed ? Object.values(CollectionsCaseStatus) : OPEN_STATUSES,
    query,
  });
  // Same key as the bank panel's first tab, so the request is shared, not doubled.
  const transfers = useBankTransactions([CollectionsBankTransactionStatus.Unmatched]);
  const sync = useSyncCollectionsCases();
  const summary = dashboard.data ?? null;
  const retryingCount = (cases.data ?? []).filter(onlyRetrying).length;
  const visibleCases = showRetrying ? (cases.data ?? []) : (cases.data ?? []).filter((row) => !onlyRetrying(row));

  // One loader for the first visit; afterwards each section shows its own when a filter changes.
  const [ready, setReady] = useState(false);
  const firstLoad = dashboard.isLoading || cases.isLoading || transfers.isLoading;
  useEffect(() => {
    if (!firstLoad) setReady(true);
  }, [firstLoad]);

  const today = dayjs().format("YYYY-MM-DD");

  const columns = useMemo(
    () => [
      {
        title: "Klientka",
        key: "client",
        render: (_: unknown, row: CollectionsCaseListItem) => (
          <div>
            <Link
              to={`/products/collections/${row.id}`}
              // The case card's back link returns to this list with its filters and page.
              state={{ back: `${location.pathname}${location.search}` }}
              className="font-medium"
            >
              {row.clientName ?? row.clientEmail}
            </Link>
            <p className="text-xs text-gray-500">
              {row.clientEmail}
              {row.balance.hasArchivedContract ? (
                <Tag className="ml-2" color="default">
                  Zarchiwizowana
                </Tag>
              ) : null}
            </p>
          </div>
        ),
      },
      {
        title: "Zaległość",
        key: "overdue",
        sorter: (a: CollectionsCaseListItem, b: CollectionsCaseListItem) =>
          a.balance.overdueAmount - b.balance.overdueAmount,
        render: (_: unknown, row: CollectionsCaseListItem) =>
          // Only a card charge Stripe still retries: not overdue yet, so not shown as debt.
          !row.balance.overdueAmount && row.balance.retryingAmount ? (
            <div>
              <p className="font-medium text-amber-600">{formatPln(row.balance.retryingAmount)}</p>
              <p className="text-xs text-gray-500">karta w ponowieniu, Stripe jeszcze próbuje</p>
            </div>
          ) : (
            <div>
              <p className="font-medium text-rose-600">{formatPln(row.balance.overdueAmount)}</p>
              <p className="text-xs text-gray-500">
                {row.balance.overdueInvoices} {pluralRaty(row.balance.overdueInvoices)}
                {row.balance.retryingInvoices ? ` · ${row.balance.retryingInvoices} w ponowieniu` : ""}
              </p>
            </div>
          ),
      },
      {
        title: "Przed nią",
        key: "future",
        render: (_: unknown, row: CollectionsCaseListItem) => (
          <div>
            <p>{formatPln(row.balance.futureAmount)}</p>
            <p className="text-xs text-gray-500">
              {row.balance.futureInstallments} {pluralRaty(row.balance.futureInstallments)}
            </p>
          </div>
        ),
      },
      {
        title: "Dni",
        dataIndex: ["balance", "daysOverdue"],
        key: "days",
        sorter: (a: CollectionsCaseListItem, b: CollectionsCaseListItem) =>
          a.balance.daysOverdue - b.balance.daysOverdue,
        render: (days: number) => <span className={days > 30 ? "font-medium text-rose-600" : ""}>{days}</span>,
      },
      {
        title: "Status",
        key: "status",
        render: (_: unknown, row: CollectionsCaseListItem) => (
          <div className="flex flex-col items-start gap-1">
            {onlyRetrying(row) ? <Tag color="gold">Karta ponawiana</Tag> : <CaseStatusTag status={row.status} />}
            <SituationTag situation={row.situation} />
          </div>
        ),
      },
      {
        title: "Następne zadanie",
        key: "next",
        render: (_: unknown, row: CollectionsCaseListItem) =>
          row.nextTask ? (
            <Tag color={row.nextTask.dueAt <= today ? "orange" : "default"}>
              {taskLabel(row.nextTask.type)} ·{" "}
              {row.nextTask.dueAt <= today ? "dziś" : dayjs(row.nextTask.dueAt).format("DD.MM")}
            </Tag>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
    ],
    [today, location.pathname, location.search],
  );

  if (!ready) {
    return (
      <div className="flex justify-center py-24">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Button onClick={() => sync.mutate()} loading={sync.isLoading}>
          Zaktualizuj sprawy
        </Button>
      </div>

      {dashboard.isError ? (
        <Alert type="error" showIcon message="Nie udało się wczytać podsumowania należności" />
      ) : (
        <div className="flex flex-col gap-6">
          <div className={KPI_GRID}>
            <Card>
              <Statistic
                title="Zaległość wymagalna"
                value={summary ? formatPln(summary.overdueAmount) : "—"}
                valueStyle={{ color: summary?.overdueAmount ? "#e11d48" : undefined }}
              />
              <p className="mt-1 text-xs text-gray-500">{summary ? `${summary.overdueCases} spraw` : "\u00a0"}</p>
            </Card>
            <Card>
              <Statistic title="Przyszłe raty dłużniczek" value={summary ? formatPln(summary.futureAmount) : "—"} />
            </Card>
            <Card>
              <Statistic
                title="Odzyskane w tym miesiącu"
                value={summary ? formatPln(summary.recoveredThisMonth) : "—"}
                valueStyle={{ color: "#059669" }}
              />
            </Card>
            <Card>
              <Statistic title="W ugodach" value={summary ? formatPln(summary.settlementAmount) : "—"} />
            </Card>
            <Card>
              <Statistic
                title="W windykacji zewnętrznej"
                value={summary ? formatPln(summary.externalCollectionAmount) : "—"}
              />
            </Card>
            <Card>
              <Statistic title="Przelewy do weryfikacji" value={summary ? summary.transfersToVerify : "—"} />
            </Card>
          </div>

          <AutomationPanel settings={summary?.automation} signature={summary?.signature} />

          <BankTransfersPanel />

          <Card>
            <Title>Do zrobienia dzisiaj</Title>
            {summary?.dueTasks.length ? (
              <>
                <div className="mt-3 flex flex-wrap gap-2">
                  {summary.dueTasks.map((entry) => (
                    <Tag key={entry.type} color="orange" className="text-sm">
                      {entry.count} × {taskLabel(entry.type)}
                    </Tag>
                  ))}
                </div>
                <Table
                  className="mt-4"
                  size="small"
                  rowKey="id"
                  columns={columns}
                  dataSource={summary.queue}
                  pagination={urlPagination(urlState.queuePage, urlState.queuePageSize, (next) =>
                    setUrlState({ queuePage: next.page, queuePageSize: next.pageSize }),
                  )}
                  scroll={{ x: true }}
                />
              </>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={summary ? "Nic na dziś" : "\u00a0"}
                className="py-6"
              />
            )}
          </Card>
        </div>
      )}

      <Card>
        <Title>Wszystkie sprawy</Title>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Input.Search
            placeholder="E-mail lub nazwisko"
            allowClear
            className="max-w-xs"
            defaultValue={query}
            onSearch={(value) => setFilter({ query: value.trim() })}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Status"
            className="min-w-[220px]"
            options={STATUS_OPTIONS}
            value={statuses}
            onChange={(value) => setFilter({ statuses: value as CaseStatus[] })}
          />
          <Checkbox checked={showRetrying} onChange={(event) => setFilter({ showRetrying: event.target.checked })}>
            Pokaż karty w ponowieniu ({retryingCount})
          </Checkbox>
          <Checkbox checked={showClosed} onChange={(event) => setFilter({ showClosed: event.target.checked })}>
            Pokaż zamknięte
          </Checkbox>
        </div>
        {cases.isError ? (
          <Alert type="error" showIcon className="mt-4" message="Nie udało się wczytać spraw" />
        ) : visibleCases.length || cases.isLoading ? (
          <Table
            className="mt-4"
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={visibleCases}
            loading={cases.isLoading}
            pagination={urlPagination(urlState.page, urlState.pageSize, (next) =>
              setUrlState({ page: next.page, pageSize: next.pageSize }),
            )}
            scroll={{ x: true }}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak spraw" className="py-10" />
        )}
      </Card>
    </div>
  );
};

export default CollectionsDashboard;
